import useCreateEmailTemplate from '@/api/mutations/emailTemplates/useCreateEmailTemplate';
import useDeleteEmailTemplate from '@/api/mutations/emailTemplates/useDeleteEmailTemplate';
import useUpdateEmailTemplate from '@/api/mutations/emailTemplates/useUpdateEmailTemplate';
import emailTemplateKeys from '@/api/queries/emailTemplates/keyFactor';
import useGetEmailTemplates from '@/api/queries/emailTemplates/useGetEmailTemplates';
import { EmailTemplateType } from '@/gql/graphql';
import AvailableEmailTemplateVariablesList from '@/ui/components/companySettings/components/AvailableEmailTemplateVariablesList';
import EmailTemplateUploadField from '@/ui/components/companySettings/components/EmailTemplateUploadField';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Modal, ModalProps, useToastContext } from '@payaca/ambrosia';
import { capitaliseFirstLetter } from '@payaca/utilities/stringUtilities';

import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import DeleteEmailTemplateModal from './DeleteEmailTemplateModal';

type AcceptedEmailTemplateType = Extract<
  EmailTemplateType,
  'PROPOSAL' | 'INVOICE'
>;

const emailTemplateTypeToLabel: Record<AcceptedEmailTemplateType, string> = {
  PROPOSAL: 'proposal',
  INVOICE: 'invoice',
};

type TFormState = z.infer<typeof formSchema>;

const formSchema = z.object({
  template: z.object({
    raw: z.string().min(1),
    preview: z.string().min(1),
  }),
});

const CreateEditEmailTemplateModalContent: FC<{
  templateType: AcceptedEmailTemplateType;
  defaultValues?: TFormState;
  isProcessing?: boolean;
  onSubmit: (state: TFormState) => void;
  onDelete: () => void;
}> = (props) => {
  const { templateType, isProcessing, onSubmit, defaultValues, onDelete } =
    props;

  const [isBlockDeleteModalOpen, setIsBlockDeleteModalOpen] = useState(false);

  const formMethods = useForm<TFormState>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  return (
    <>
      <form onSubmit={formMethods.handleSubmit(onSubmit, console.error)}>
        <Modal.Body className="space-y-4">
          <Controller
            render={({ field: { onChange, value } }) => {
              return (
                <EmailTemplateUploadField
                  label={`${capitaliseFirstLetter(emailTemplateTypeToLabel[templateType])} template`}
                  downloadFileName={`${emailTemplateTypeToLabel[templateType]}-email-template`}
                  validationState={
                    formMethods.formState.errors.template
                      ? {
                          isValid: false,
                          validationMessages: ['Template is required'],
                        }
                      : undefined
                  }
                  type={templateType}
                  value={value}
                  onChange={(value) => onChange(value || null)}
                />
              );
            }}
            name={'template'}
            control={formMethods.control}
          />

          <AvailableEmailTemplateVariablesList
            emailTemplateType={templateType}
          />
        </Modal.Body>

        <Modal.Footer>
          <Modal.Footer.Actions>
            {defaultValues?.template && (
              <Button
                disabled={isProcessing}
                isProcessing={isProcessing}
                variant="secondary"
                colour="negative"
                onClick={() => setIsBlockDeleteModalOpen(true)}
              >
                Delete
              </Button>
            )}

            <Button
              disabled={!formMethods.formState.isDirty || isProcessing}
              isProcessing={isProcessing}
              type="submit"
            >
              {defaultValues?.template
                ? 'Save'
                : `Create ${emailTemplateTypeToLabel[templateType]} email template`}
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </form>
      <DeleteEmailTemplateModal
        isOpen={isBlockDeleteModalOpen}
        onClose={() => setIsBlockDeleteModalOpen(false)}
        onDelete={onDelete}
      />
    </>
  );
};

type TProps = {
  templateType: AcceptedEmailTemplateType;
} & Omit<ModalProps, 'title'>;

const CreateEditEmailTemplateModal: FC<TProps> = (props) => {
  const { templateType, onClose, ...rest } = props;

  const { pushError } = useToastContext();

  const { data: emailTemplates, isPending: isLoadingExistingTemplate } =
    useGetEmailTemplates({
      types: [templateType],
    });
  const existingTemplate = emailTemplates?.emailTemplates[0];

  const queryClient = useQueryClient();
  const { mutateAsync: createEmailTemplate, isPending: isCreateTemplate } =
    useCreateEmailTemplate();

  const { mutateAsync: updateEmailTemplate, isPending: isUpdateTemplate } =
    useUpdateEmailTemplate();

  const {
    mutateAsync: deleteEmailTemplateMutation,
    isPending: isDeletingTemplate,
  } = useDeleteEmailTemplate();

  const handleSubmit = async (state: TFormState) => {
    if (!state.template) {
      return;
    }

    try {
      if (existingTemplate) {
        await updateEmailTemplate({
          publicId: existingTemplate.id,
          name: existingTemplate.name,
          template: state.template.raw,
        });
      } else {
        await createEmailTemplate({
          name: templateType,
          type: templateType,
          template: state.template.raw,
        });
      }
    } catch (e) {
      pushError('Failed to update email template');
    }

    await queryClient.invalidateQueries({
      queryKey: emailTemplateKeys.emailTemplates(),
    });

    onClose?.();
  };

  const handleDelete = async () => {
    if (!existingTemplate) {
      return;
    }

    try {
      await deleteEmailTemplateMutation({
        publicId: existingTemplate.id,
      });
    } catch (e) {
      pushError('Failed to delete email template');
    }

    await queryClient.invalidateQueries({
      queryKey: emailTemplateKeys.emailTemplates(),
    });

    onClose?.();
  };

  return (
    <Modal
      title={`${existingTemplate ? 'Edit' : 'Create'} ${emailTemplateTypeToLabel[templateType]} email template`}
      size="lg"
      onClose={onClose}
      {...rest}
    >
      {isLoadingExistingTemplate ? (
        <>Loading...</>
      ) : (
        <CreateEditEmailTemplateModalContent
          onSubmit={handleSubmit}
          defaultValues={
            existingTemplate
              ? {
                  template: {
                    raw: existingTemplate.template,
                    preview: existingTemplate.preview,
                  },
                }
              : undefined
          }
          templateType={templateType}
          isProcessing={
            isCreateTemplate || isUpdateTemplate || isDeletingTemplate
          }
          onDelete={handleDelete}
        />
      )}
    </Modal>
  );
};

export default CreateEditEmailTemplateModal;
